<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="isOpen" class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" @click.self="close">
        <div class="relative w-full max-w-4xl rounded-2xl overflow-hidden shadow-lg bg-white">
          <!-- Regular Slide Preview -->
          <div v-if="!item.isFinalSlide" class="flex">
            <!-- Left side - Text content -->
            <div class="flex-1 p-8 space-y-6">
              <h1 class="text-4xl font-bold leading-tight">
                {{ item.title }}
              </h1>
              <p class="text-lg leading-relaxed">
                {{ item.description }}
              </p>
              <div v-if="item.question" class="space-y-4">
                <h3 class="text-xl font-medium">
                  {{ item.question }}
                </h3>
                <div v-if="item.checkboxes && item.checkboxes.length" class="grid grid-cols-2 gap-3">
                  <label v-for="checkbox in item.checkboxes" :key="checkbox" class="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" class="w-4 h-4 rounded border-gray-300">
                    <span class="text-base">{{ checkbox }}</span>
                  </label>
                </div>
              </div>
            </div>
            <!-- Right side - Image -->
            <div v-if="item.image" class="flex-1 bg-gray-100 flex items-center justify-center p-8">
              <img :src="item.image" alt="Slide image" class="max-w-full max-h-full object-contain">
            </div>
          </div>

          <!-- Final Slide Preview -->
          <div v-else class="flex">
            <!-- Left side - Final slide content -->
            <div class="flex-1 p-8 space-y-6">
              <h1 v-if="item.finalSlideConfig?.title" class="text-4xl font-bold leading-tight">
                {{ item.finalSlideConfig.title }}
              </h1>

              <p v-if="item.finalSlideConfig?.description" class="text-lg leading-relaxed">
                {{ item.finalSlideConfig.description }}
              </p>

              <!-- Social Cards Layout -->
              <div class="flex flex-col md:flex-row gap-8 mt-6">
                <!-- Telegram Card -->
                <div v-if="item.finalSlideConfig?.showTelegramButton !== false || item.finalSlideConfig?.showTelegramQR !== false" class="flex flex-row items-center gap-x-3">
                  <!-- QR Code -->
                  <div v-if="item.finalSlideConfig?.showTelegramQR !== false" class="w-16 h-16 bg-white border border-black rounded-xl p-2 flex items-center justify-center">
                    <div class="w-full h-full bg-gray-300 rounded flex items-center justify-center">
                      <span class="text-xs text-gray-600">QR</span>
                    </div>
                  </div>
                  <!-- Button -->
                  <div v-if="item.finalSlideConfig?.showTelegramButton !== false" class="text-center">
                    <button class="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#27a3e2] text-white font-semibold text-sm">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-.38.24-1.96 1.28-.29.2-.55.3-.77.29-.25-.01-.74-.14-1.1-.26-.44-.15-.79-.23-.76-.49.02-.14.18-.28.48-.42 1.92-.84 3.2-1.39 3.84-1.66 1.54-.65 1.86-.76 2.07-.76.05 0 .15.01.21.09.05.06.06.14.07.19z" />
                      </svg>
                      Telegram
                    </button>
                  </div>
                </div>

                <!-- WhatsApp Card -->
                <div v-if="item.finalSlideConfig?.showWhatsappButton !== false || item.finalSlideConfig?.showWhatsappQR !== false" class="flex flex-row items-center gap-x-3">
                  <!-- QR Code -->
                  <div v-if="item.finalSlideConfig?.showWhatsappQR !== false" class="w-16 h-16 bg-white border border-black rounded-xl p-2 flex items-center justify-center">
                    <div class="w-full h-full bg-gray-300 rounded flex items-center justify-center">
                      <span class="text-xs text-gray-600">QR</span>
                    </div>
                  </div>
                  <!-- Button -->
                  <div v-if="item.finalSlideConfig?.showWhatsappButton !== false" class="text-center">
                    <button class="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[#24cc63] text-white font-semibold text-sm">
                      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                      </svg>
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              <p v-if="item.finalSlideConfig?.shortDescription" class="text-base text-gray-600">
                {{ item.finalSlideConfig.shortDescription }}
              </p>
            </div>
            <!-- Right side - Image -->
            <div v-if="item.finalSlideConfig?.image" class="flex-1 bg-gray-100 flex items-center justify-center p-8">
              <img :src="item.finalSlideConfig.image" alt="Final slide image" class="max-w-full max-h-full object-contain">
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  modelValue: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits([ 'update:modelValue' ]);

const isOpen = ref(props.modelValue);

watch(() => props.modelValue, val => {
  isOpen.value = val;
});

function close() {
  emit('update:modelValue', false);
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
